import { useState, useRef, useCallback } from 'react';
import { Upload, Download, FileSpreadsheet, ArrowLeft, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import type { CsvEntityConfig, BulkUploadResult, BulkUploadRowError } from '../types';
import { parseCsvFile } from '../utils/csvParser';
import { downloadCsvTemplate } from '../utils/csvTemplateGenerator';
import { validateCsvRows } from '../utils/csvValidator';
import { CsvPreviewTable } from './CsvPreviewTable';
import { BulkUploadResults } from './BulkUploadResults';

type Step = 'upload' | 'preview' | 'results';

interface BulkUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: CsvEntityConfig;
  onComplete?: () => void;
}

export function BulkUploadDialog({ open, onOpenChange, config, onComplete }: BulkUploadDialogProps) {
  const [step, setStep] = useState<Step>('upload');
  const [rawData, setRawData] = useState<Record<string, string>[]>([]);
  const [validRows, setValidRows] = useState<Record<string, any>[]>([]);
  const [validationErrors, setValidationErrors] = useState<BulkUploadRowError[]>([]);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setRawData([]);
    setValidRows([]);
    setValidationErrors([]);
    setResult(null);
    setIsSubmitting(false);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleFileSelect = async (file: File) => {
    setParseError(null);
    if (!file.name.endsWith('.csv')) {
      setParseError('Please select a CSV file.');
      return;
    }

    try {
      const parsed = await parseCsvFile(file);

      if (parsed.data.length === 0) {
        setParseError('The CSV file is empty or has no data rows.');
        return;
      }

      if (parsed.data.length > 500) {
        setParseError('Maximum 500 rows per upload. Please split your file.');
        return;
      }

      // Check that required columns exist
      const headers = Object.keys(parsed.data[0] || {});
      const requiredCols = config.columns.filter(c => c.required).map(c => c.key);
      const missingCols = requiredCols.filter(col => !headers.includes(col));
      if (missingCols.length > 0) {
        setParseError(`Missing required columns: ${missingCols.join(', ')}`);
        return;
      }

      setRawData(parsed.data);

      const { validRows: vRows, errors } = validateCsvRows(parsed.data, config);
      setValidRows(vRows);
      setValidationErrors(errors);
      setStep('preview');
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse CSV file.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async () => {
    if (validRows.length === 0) return;
    setIsSubmitting(true);
    try {
      const res = await config.bulkCreateAction({ rows: validRows });
      setResult(res);
      setStep('results');
      if (res.created > 0 && onComplete) onComplete();
    } catch (err: any) {
      setResult({
        created: 0,
        skipped: validRows.length,
        errors: [{ row: 0, message: err.message || 'Server error during upload' }],
        total: validRows.length,
      });
      setStep('results');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'upload' && `Bulk Upload ${config.entityName}`}
            {step === 'preview' && `Preview - ${config.entityName}`}
            {step === 'results' && `Upload Results - ${config.entityName}`}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a CSV file to create multiple records at once.'}
            {step === 'preview' && 'Review parsed data before creating records.'}
            {step === 'results' && 'Upload complete. See results below.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <button
              onClick={() => downloadCsvTemplate(config)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Download className="w-4 h-4" />
              Download CSV Template
            </button>

            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Drop your CSV file here or <span className="text-blue-600 dark:text-blue-400">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">Maximum 500 rows per upload</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
            </div>

            {parseError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {parseError}
              </div>
            )}

            {config.importNotes && config.importNotes.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Notes:</p>
                <ul className="text-xs text-amber-600 dark:text-amber-500 space-y-0.5">
                  {config.importNotes.map((note, idx) => (
                    <li key={idx}>- {note}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Expected columns:</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Columns marked with * are required.</p>
              <div className="flex flex-wrap gap-1.5">
                {config.columns.map(col => (
                  <span
                    key={col.key}
                    className={`text-xs px-2 py-0.5 rounded ${
                      col.required
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}
                    title={col.description}
                  >
                    {col.key}{col.required && '*'}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">{rawData.length}</span> rows parsed,{' '}
                <span className="font-medium text-green-600">{validRows.length}</span> valid
                {validationErrors.length > 0 && (
                  <>, <span className="font-medium text-red-600">{validationErrors.length}</span> errors</>
                )}
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs text-yellow-700 dark:text-yellow-400">
                Rows with validation errors will be skipped. Only valid rows will be uploaded.
              </div>
            )}

            <CsvPreviewTable
              columns={config.columns}
              data={rawData}
              errors={validationErrors}
            />

            <div className="flex justify-between pt-2">
              <button
                onClick={() => { reset(); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={validRows.length === 0 || isSubmitting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {validRows.length} {validRows.length === 1 ? 'Record' : 'Records'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 'results' && result && (
          <div className="space-y-4">
            <BulkUploadResults result={result} config={config} />
            <div className="flex justify-end pt-2">
              <button
                onClick={() => handleOpenChange(false)}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



